import { useQuery } from '@tanstack/react-query';
import { GoalIcon, PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { useAuthStore } from '@/shared/auth/store';
import { normalizeQuery } from '@/shared/lib/strings';
import type { CommandItem } from '@/shared/registry/types';

import { GOALS_PATH, goalsApi, mapPath, useGoalCounters } from '../api';

/** Sidebar badge: the user's open goals whose deadline passed (refreshed by realtime events). */
export function useGoalsNavCounter(): number | undefined {
  const signedIn = useAuthStore((s) => s.status === 'authenticated');
  const overdue = useGoalCounters({ enabled: signedIn }).data?.overdue;
  return overdue && overdue > 0 ? overdue : undefined;
}

/** ⌘K: maps found by title, and "New map" while the query is empty or names it. */
export function useGoalCommands(query: string): { items: CommandItem[]; isLoading?: boolean } {
  const { t } = useTranslation('goals');
  const navigate = useNavigate();
  const search = normalizeQuery(query) ?? '';
  const enabled = search.length >= 2;
  const found = useQuery({
    queryKey: ['goals', 'maps', 'palette', search],
    queryFn: ({ signal }) => goalsApi.maps('all', search, undefined, signal),
    enabled,
    staleTime: 15_000,
  });
  // The goals have no menu entry (they are a tab of the Tasks page), so the palette is how a person jumps straight to them.
  const open: CommandItem = {
    id: 'goals:open',
    label: t('commands.open'),
    icon: GoalIcon,
    keywords: [t('nav.title'), 'goal', 'goals', 'map', 'цель', 'цели', 'карта', 'ціль', 'цілі'],
    to: GOALS_PATH,
  };
  const create: CommandItem = {
    id: 'goals:new',
    label: t('commands.newMap'),
    icon: PlusIcon,
    keywords: [t('nav.title'), 'goal', 'map', 'цель', 'карта', 'ціль', 'mind map', 'okr'],
    perform: () => void navigate(`${GOALS_PATH}?create=1`),
  };
  const items: CommandItem[] = enabled
    ? (found.data ?? []).slice(0, 6).map((map) => ({
        id: `goals-map:${map.id}`,
        label: map.title,
        description: t('commands.mapDescription', { owner: map.owner.fullName, progress: map.progress }),
        icon: GoalIcon,
        to: mapPath(map.id),
      }))
    : [];
  return { items: [...items, open, create], isLoading: enabled && found.isLoading };
}
